import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/api/axiosInstance'

const hasEnabledSlots = (availabilitySchedule: any): boolean => {
    if (!availabilitySchedule || typeof availabilitySchedule !== 'object') {
        return false
    }

    return Object.values(availabilitySchedule).some((day: any) => {
        if (!day || typeof day !== 'object') return false
        const enabled = Boolean(day.enabled)
        const slots = Array.isArray(day.slots) ? day.slots : []
        return enabled && slots.length > 0
    })
}

export const useProfile = () => {
    return useQuery({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await axiosInstance.get('/profile/')
            return response.data 
        },
    })
}

export const useProviderProfile = () => {
    return useQuery({
        queryKey: ['provider-profile'],
        queryFn: async () => {
            const response = await axiosInstance.get('/provider-profile/')
            return response.data
        },
        retry: false,
    })
}

export const useProfileStatus = () => {
    return useQuery({
        queryKey: ['profile-status'],
        queryFn: async () => {
            try {
                // Fetch profile, provider profile, and service offerings
                const [profileResponse, providerResponse, offeringsResponse] = await Promise.all([
                    axiosInstance.get('/profile/').catch((err) => {
                        console.warn('Failed to fetch profile:', err)
                        return null
                    }),
                    axiosInstance.get('/provider-profile/').catch((err) => {
                        console.warn('Failed to fetch provider-profile:', err)
                        return null
                    }),
                    axiosInstance.get('/service-offerings/my_offerings/').catch((err) => {
                        console.warn('Failed to fetch service-offerings:', err)
                        return { data: [] }
                    }),
                ])

                const profile = profileResponse?.data
                const providerProfile = providerResponse?.data
                const serviceOfferings = Array.isArray(offeringsResponse?.data) ? offeringsResponse.data : []

                if (!providerProfile) {
                    console.log('No provider profile found')
                    return {
                        is_verified: false,
                        has_districts: false,
                        has_availability: false,
                        has_pricing: false,
                        has_personal_info: false,
                        has_services: false,
                        is_profile_configured: false,
                        can_post_services: false,
                        is_provider: false,
                    }
                }

                // Check personal info
                const hasPersonalInfo = Boolean(
                    profile?.first_name && 
                    profile?.last_name && 
                    profile?.national_id_number
                )

                // Check districts
                const hasDistricts = Array.isArray(providerProfile.service_districts) && providerProfile.service_districts.length > 0

                // Check services (at least one service offering)
                const hasServices = serviceOfferings.length > 0

                // Check pricing configuration (all offerings must have proper pricing)
                const hasPricing = hasServices && serviceOfferings.every((offering: any) => {
                    if (offering.pricing_type === 'hourly') {
                        const hasRate = Boolean(offering.hourly_rate)
                        const hasMinHours = Boolean(offering.minimum_hours)
                        return hasRate && hasMinHours
                    }
                    return true // Quote-based services don't need hourly_rate
                })

                // Check availability
                const hasAvailability = hasEnabledSlots(providerProfile.availability_schedule)

                // Check verification
                const isVerified = Boolean(providerProfile.is_verified)

                // All steps must be complete for profile to be configured
                const isProfileConfigured = hasPersonalInfo && hasDistricts && hasServices && hasPricing && hasAvailability

                console.log('Profile Status Calculation:', {
                    hasPersonalInfo,
                    hasDistricts,
                    hasServices,
                    hasPricing,
                    hasAvailability,
                    isVerified,
                    isProfileConfigured,
                    serviceOfferingsCount: serviceOfferings.length,
                    districtsCount: providerProfile.service_districts?.length || 0,
                })

                return {
                    is_verified: isVerified,
                    has_districts: hasDistricts,
                    has_availability: hasAvailability,
                    has_pricing: hasPricing,
                    has_personal_info: hasPersonalInfo,
                    has_services: hasServices,
                    is_profile_configured: isProfileConfigured,
                    can_post_services: isVerified,
                    is_provider: true,
                }
            } catch (error: any) {
                console.error('Error in useProfileStatus:', error)
                if (error.response?.status === 404) {
                    return {
                        is_verified: false,
                        has_districts: false,
                        has_availability: false,
                        has_pricing: false,
                        has_personal_info: false,
                        has_services: false,
                        is_profile_configured: false,
                        can_post_services: false,
                        is_provider: false,
                    }
                }
                throw error
            }
        },
        retry: false,
        staleTime: 0, // Always consider data stale, refetch on mount
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    })
}

export const useUpdateProfile = () => {
    const queryClient = useQueryClient()
    
    return useMutation({
        mutationFn: async (data: any) => {
            const response = await axiosInstance.patch('/profile/', data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] })
            queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        },
    })
}

export const useUpdateProviderProfile = () => {
    const queryClient = useQueryClient()
    
    return useMutation({
        mutationFn: async (data: any) => {
            const response = await axiosInstance.patch('/provider-profile/', data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provider-profile'] })
            queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        },
    })
}


